// Shipping rate analysis types
export interface Rate {
  rateId: string;
  rateType?: string;
  carrierId?: string;
  carrierCode?: string;
  carrierFriendlyName: string;
  carrierNickname?: string;
  serviceCode: string;
  serviceName?: string;
  serviceType?: string;
  packageType?: string;
  shippingAmount: { amount: number; currency: string };
  insuranceAmount?: { amount: number; currency: string };
  confirmationAmount?: { amount: number; currency: string };
  otherAmount?: { amount: number; currency: string };
  taxAmount?: { amount: number; currency: string };
  zone?: number;
  deliveryDays?: number;
  guaranteedService: boolean;
  estimatedDeliveryDate?: string;
  carrierDeliveryDays?: string;
  shipDate?: string;
  negotiatedRate?: boolean;
  trackable?: boolean;
  validationStatus?: string;
  warningMessages?: string[];
  errorMessages?: string[];
}

export interface RateScores {
  overall: number;
  price: number;
  speed: number;
  reliability: number;
  value: number;
  carrierTrust: number;
  convenience: number;
}

export interface RateRankings {
  byPrice: number;
  bySpeed: number;
  byValue: number;
  byOverall: number;
}

export interface TagSystem {
  primary: PrimaryTag | null;
  secondary: SecondaryTag[];
  contextual: ContextualTag[];
  badges: BadgeTag[];
}

export enum PrimaryTag {
  BEST_OVERALL = "Best Overall",
  CHEAPEST = "Cheapest",
  FASTEST = "Fastest",
  RECOMMENDED = "Recommended"
}

export enum SecondaryTag {
  GREAT_VALUE = "Great Value",
  EXPRESS = "Express",
  ECONOMY = "Economy",
  PREMIUM = "Premium",
  STANDARD = "Standard"
}

export enum ContextualTag {
  ARRIVES_TOMORROW = "Arrives Tomorrow",
  WEEKEND_DELIVERY = "Weekend Delivery",
  GUARANTEED_TIME = "Guaranteed Time",
  FREE_TRACKING = "Free Tracking",
  SIGNATURE_AVAILABLE = "Signature Available",
  SAME_DAY = "Same Day",
  NEXT_DAY = "Next Day",
  TWO_DAY = "2-Day Delivery"
}

export enum BadgeTag {
  ECO_FRIENDLY = "🌱 Eco-Friendly",
  LOCAL_CARRIER = "📍 Local Carrier",
  NEGOTIATED_RATE = "💰 Special Rate",
  FLAT_RATE = "📦 Flat Rate",
  MOST_POPULAR = "⭐ Most Popular",
  TRUSTED_CARRIER = "✓ Trusted",
  GROUND_SHIPPING = "🚚 Ground",
  AIR_SHIPPING = "✈️ Air"
}

export interface RateAnalysis {
  scores: RateScores;
  rankings: RateRankings;
  tags: TagSystem;
  recommendation: RecommendationLevel;
}

export enum RecommendationLevel {
  HIGHLY_RECOMMENDED = "highly_recommended",
  RECOMMENDED = "recommended",
  ACCEPTABLE = "acceptable",
  NOT_RECOMMENDED = "not_recommended"
}

export interface AnalyzedRate extends Rate {
  analysis: RateAnalysis;
}

export interface UserShippingPreferences {
  customerId: string;
  speedWeight: number; // 0-1
  priceWeight: number; // 0-1
  reliabilityWeight: number; // 0-1
  preferredCarriers: string[];
  avoidCarriers: string[];
  maxAcceptablePrice?: number;
  preferFlatRate: boolean;
  ecoFriendlyPreferred: boolean;
  preferredDeliveryDays?: number;
}

export interface OrderContext {
  orderId?: number;
  orderValue?: number;
  customerType?: 'new' | 'returning' | 'vip';
  urgency?: 'low' | 'normal' | 'high';
  destinationZone?: number;
  itemTypes?: string[];
}

export interface TagDisplay {
  text: string;
  color: string;
  textColor?: string;
  priority: number;
  icon?: string;
  outlined?: boolean;
  small?: boolean;
}

export interface PriceStatistics {
  min: number;
  max: number;
  mean: number;
  median: number;
  stdDev: number;
  percentiles: {
    p25: number;
    p50: number;
    p75: number;
  };
}

export interface SpeedStatistics {
  min: number;
  max: number;
  mean: number;
  fastestCarriers: string[];
}

// Label layout types and constants
export type LabelLayout = '4x6' | '4x8' | 'letter';

export const LABEL_LAYOUTS: { text: string; value: LabelLayout }[] = [
  { text: '4x6 - Standard Shipping', value: '4x6' },
  { text: '4x8 - Extended Shipping', value: '4x8' },
  { text: '8.5x11 (Letter)', value: 'letter' }
];

export const LABEL_DIMENSIONS: Record<LabelLayout, { width: number; height: number; widthMm: number; heightMm: number }> = {
  '4x6': { width: 4, height: 6, widthMm: 101.6, heightMm: 152.4 },
  '4x8': { width: 4, height: 8, widthMm: 101.6, heightMm: 203.2 },
  'letter': { width: 8.5, height: 11, widthMm: 215.9, heightMm: 279.4 }
};

export const DEFAULT_LABEL_LAYOUT: LabelLayout = '4x6';